import { formErrorMessages } from '../messages/opacc.messages.js';
import { OpaccZipSearch } from './opacc.class.zipsearch.js';
import { OpaccXssValidator } from './opacc.class.xssValidator.js';

/** @module form */

/**
 * Funktionalität zur Formularvalidierung
 * @param {HTMLElement} form Formular
 * @param {Function|Array} [customPostValidationMethod=null] Zusätzliche, benutzerdefinierte Formularvalidierungen
 *
 */
export class OpaccFormValidator {
	constructor(form, customPostValidationMethod = null) {
		this.form = form;
		this.messages = formErrorMessages;
		this.customPostValidation = customPostValidationMethod;
		this.pwdValidationElement = this.form.querySelector('.pwd-strength');

		this.zipSearch = new OpaccZipSearch(form);
		this.xssValidation = new OpaccXssValidator(form);

        let emailPattern = '^[\\w\\-.+]+@([A-Za-z0-9_\\-]+\\.)+[A-Za-z]{2,}$';
		let pwdNumberPattern = window.opacc.pwdPattern && window.opacc.pwdPattern.numberPattern ? window.opacc.pwdPattern.numberPattern : '(?=.*([\\d]))';
		let pwdUpperPattern = window.opacc.pwdPattern && window.opacc.pwdPattern.upperPattern ? window.opacc.pwdPattern.upperPattern : '(?=.*([A-Z]))';
		let pwdLengthPattern = window.opacc.pwdPattern && window.opacc.pwdPattern.minLengthPattern && window.opacc.pwdPattern.maxLengthPattern ? `(.{${window.opacc.pwdPattern.minLengthPattern},${window.opacc.pwdPattern.maxLengthPattern}})` : '(.{9,})';
		let pwdCharacterPattern = window.opacc.pwdPattern && window.opacc.pwdPattern.allowedCharacterPattern ? `${window.opacc.pwdPattern.allowedCharacterPattern}+` : '.+';
		let pwdRegex = window.opacc.pwdPattern && window.opacc.pwdPattern.regex ? window.opacc.pwdPattern.regex : '';

		this.emailPattern = emailPattern;
		this.pwdPatternObj = {
			pwdNumberPattern: {
				pattern: pwdNumberPattern,
				match: false },
			pwdUpperPattern: {
				pattern: pwdUpperPattern,
				match: false },
			pwdLengthPattern: {
				pattern: pwdLengthPattern,
				match: false },
			pwdCharacterPattern: {
				pattern: `^${pwdCharacterPattern}$`,
				match: false }
		};

		this.pwdPattern = pwdRegex.length > 0 ? pwdRegex : `^${pwdNumberPattern}${pwdUpperPattern}${pwdLengthPattern}$`;

		if (!this.form.hasAttribute('novalidate')) {
			this.form.noValidate = true;
			this.inlineValidation = (/true/i).test(this.form.dataset.opValidateInline) || false;

			this.attachEventListener(this.inlineValidation);
		}
	}

	/**
	 * Event Registrierungen im DOM
	 *
	 * @param {boolean} inline Sollen die Formularfelder innerhalb des Formulares beim Fokusverlust validiert werden?
	 */
	attachEventListener(inline) {
		this.form.addEventListener('submit', e => {
			e.preventDefault();
			const submitBtns = Array.from(this.form.elements).filter(item => { return item.type === 'submit';});

			this.validateForm(this.form.elements).then(result => {
				// disable Submit Button wenn validation ok
				// (enable nicht notwendig, da nach aktualisierung vom response das attribut nicht mehr vorhanden ist)
				if(!result.length) {
					submitBtns.forEach(submitBtn => {
						submitBtn.disabled = true;
					});
				}

				//inject custom Validation
				if (this.customPostValidation != null && (typeof this.customPostValidation == 'function' || typeof this.customPostValidation == 'object')) {
					if (typeof this.customPostValidation == 'function') {
						result.push(...this.customPostValidation());
					}
					else {
						this.customPostValidation.forEach(func => {
							result.push(...func());
						});
					}
				}

				// if no Errors --> submit form / else show error
				if (result.length === 0) {
					this.xssValidation.htmlEncodeXssFields();

                    let captchaObj = this.checkGRecaptcha(this.form);
					if (captchaObj.gRecaptchaElem && captchaObj.gRecaptchaApi) {
						// deprecated --> used for Google ReCAPTCHA V2
						if (captchaObj.isInvisibleCaptcha) {
							window.grecaptcha.execute();
						} else {
							let captchaResp = this.form.querySelector('#g-recaptcha-response');
							if (captchaResp && captchaResp.value != '') {
								this.form.submit();
							}
						}
					} else if (captchaObj.hasDedicatedCheck && captchaObj.reCaptchaResponse === '') {
						return;
					} else {
						this.form.submit();
					}
				} else {
					this.showError(result);
					// set focus on first formElement with Error
					result[0].item.focus();
				}
			});
		});

		if (this.pwdValidationElement) {
			let passwordFields = this.form.querySelectorAll('input[type="password"][data-op-check-pwd-strength="true"]');
			Array.from(passwordFields).forEach(item => {
				['focus','keyup'].forEach( e => {
					item.addEventListener(e, () => {
						this.checkPasswordPattern(item);
					});
				});
			});
		}

		if (inline) {
			this.validateInline(this.form.elements);
		}
	}

	/**
	 * Validierung der Formularfelder beim Verlassen prüfen
	 *
	 * @param {HTMLElement} formElems Formularfelder innerhalb des Formulares
	 */
	validateInline(formElems) {
		Array.from(formElems).forEach( item => {
			// // Don't validate submits, buttons, file and reset inputs, disabled and hidden fields
			if (item.disabled || item.type === 'file' || item.type === 'reset' || item.type === 'submit' || item.type === 'button' ||  item.type === 'hidden' && !item.hasAttribute('data-op-value')) return;

			// Validation for checkboxes and radio's
			item.addEventListener( 'click', () => {
				if (!(item.type === 'checkbox' || item.type === 'radio')) return;

				if (!item.pattern) {
					this.setItemPattern(item);
				}

				this.removeError(item);

				// if results --> show error
				this.validateInput(item).then((result) => {
					// custom Function injection
					if (this.customPostValidation != null && (typeof this.customPostValidation == 'function' || typeof this.customPostValidation == 'object')) {
						if (typeof this.customPostValidation == 'function') {
							result.push(...this.customPostValidation());
						}
						else {
							this.customPostValidation.forEach(func => {
								result.push(...func());
							});
						}
					}

					if (result.length != 0) {
						this.showError(result);
					}
				});
			});

			if (item.dataset.opZipcitySearch) {
				item.addEventListener('focus', () => {
					let value = item.value.trim();
					if (!value) return;

					let zipCityField = this.zipSearch.inputFields.filter(obj => {
						return obj.inputField == item;
					});

					let zipCity = zipCityField[0].data.find(i => value === `${i.zip} ${i.city}`);

					if(!zipCity){
						item.nextElementSibling.classList.remove('is-visible');
					}else{
						this.removeError(item);
					}
				});
			}

			//check if Formfield is blurred by Key "Tab"
			let tabbed = undefined;
			item.addEventListener('keydown', (e) => {
				tabbed = false;
				if (e.keyCode == 9) {
					tabbed = true;
				}
			});

			item.addEventListener('blur', (e) => {
				if (!item.pattern) {
					this.setItemPattern(item);
				}

				this.removeError(item);

				// if results --> show error
				this.validateInput(item).then((result) => {
					// custom Function injection
					if (this.customPostValidation != null && (typeof this.customPostValidation == 'function' || typeof this.customPostValidation == 'object')) {
						if (typeof this.customPostValidation == 'function') {
							result.push(...this.customPostValidation());
						}
						else {
							this.customPostValidation.forEach(func => {
								result.push(...func());
							});
						}
					}

					if (result.length != 0) {
						this.showError(result);
					} else if (e.relatedTarget && e.relatedTarget.type === 'submit') {
						e.relatedTarget.focus();
						if (!tabbed) {
							e.relatedTarget.click();
						}
					}
				});
			});
		});
	}

	/**
	 * Formular auf Gültigkeit prüfen beim submit
	 *
	 * @param {HTMLElement} formElems Formularelemente des zu übermittelnden Formulares
	 * @returns Promise mit fehlerhaften Formularelementen und Fehlerbeschreibung zur Weiterverarbeitung
	 */
	validateForm(formElems) {
        let validationSet = [];
		Array.from(formElems).forEach( item => {
            // Don't validate submits, buttons, file and reset inputs, disabled and hidden fields
			if (item.disabled || item.type === 'file' || item.type === 'reset' || item.type === 'submit' || item.type === 'button' ||  item.type === 'hidden' && !item.hasAttribute('data-op-value')) return;

			if (!item.pattern) {
                this.setItemPattern(item);
			}

			this.removeError(item);

			this.validateInput(item).then(result => {
                if (result.length != 0) {
                    validationSet.push(result[0]);
				}
			});

		});

		return new Promise((resolve) => {
			resolve(validationSet);
		});
	}

	/**
	 * Prüfung eines einzelnen Formularfeldes ohne Formwrapper
	 *
	 * @param {HTMLElement} item Formularfeld, welches geprüft werden soll
	 * @returns Promise ob Feld gültig ist (true / false)
	 */
	validateField(item) {
		// // Don't validate submits, buttons, file and reset inputs, and disabled fields
		if (item.disabled || item.type === 'file' || item.type === 'reset' || item.type === 'submit' || item.type === 'button') return;

		if (this.pwdValidationElement) {
			['focus','keyup'].forEach( e => {
				item.addEventListener(e, () => {
					this.checkPasswordPattern(item);
				});
			});
		}

		if (!item.pattern) {
			this.setItemPattern(item);
		}

		this.removeError(item);

		// if results --> show error
		let isInputValid = this.validateInput(item).then((result) => {
			if (result.length != 0) {
				this.showError(result);
				return false;
			} else {
				return true;
			}
		});
		return Promise.resolve(isInputValid);
	}

	/**
	 * Input-Felder vom Typ Email und Password mit entsprechendem pattern zur Validierung vorbereiten
	 *
	 * @param {HTMLElement} item Formularfeld
	 */
	setItemPattern(item) {
		if (item.type === 'email') { item.pattern = this.emailPattern; }
		if (item.type === 'password') { item.pattern = this.pwdPattern; }
	}

	/**
	 * Anzeige des Status der Passwort-Validierungs-Richtlinien im DOM aktualisieren
	 *
	 * @param {HTMLElement} item Inputfeld vom Typ "password"
	 */
	checkPasswordPattern(item) {
		let conditions = this.pwdValidationElement.querySelectorAll('[data-op-pwd-condition]');
		Object.keys(this.pwdPatternObj).forEach(key => {
			let regex = new RegExp(this.pwdPatternObj[key].pattern);
			this.pwdPatternObj[key].match = regex.test(item.value);
			if (conditions) {
				if (this.pwdPatternObj[key].match) {
					Array.from(conditions).filter(i => {
						this.camelCase(i.dataset.opPwdCondition) === key ? i.classList.add('is-valid') : '';
					});
				} else {
					Array.from(conditions).filter(i => {
						this.camelCase(i.dataset.opPwdCondition) === key ? i.classList.remove('is-valid') : '';
					});
				}
			}
		});
	}

	isMatchingInput(item) {
		this.removeError(item);
		this.removeError(this.form.querySelector(item.dataset.opMatchInput));
		let compareInput = this.form.querySelector(item.dataset.opMatchInput) ? this.form.querySelector(item.dataset.opMatchInput).value : '';
		if (compareInput !== '' && item.value !== '') {
			return item.value === compareInput;
		}

		return undefined;
	}

	/**
	 * // ToDO:
	 *
	 * @returns String // ToDo
	 */
	keydownEvent() {
		let evt = window.event;
		let charCode = evt.keyCode || evt.which;
		let charStr = String.fromCharCode(charCode);
		return charStr;
	}

	camelCase(string) {
		return string.replace( /-([a-z])/ig, ( all, letter ) => {
			return letter.toUpperCase();
		});
	}

	/**
	 * Validierungs-Details für Formularfeld überprüfen und bei Fehler entsprechende Meldung zurückgeben
	 *
	 * @param {HTMLElement} item Formularfeld
	 * @returns Fehlermeldung als String
	 */
	getValidityDetails(item) {
		let validity = item.validity;
		if (validity.valueMissing) {
			if (item.dataset.opMessageValueMissing) return item.dataset.opMessageValueMissing;
			if (item.type === 'select-multiple') return this.messages.messageValueMissingSelectMulti.text;
			if (item.type === 'select-one') return this.messages.messageValueMissingSelect.text;
			return this.messages.messageValueMissing.text;
		}

		// If not the right type
		if (validity.typeMismatch) {
			// Email
			if (item.dataset.opMessageTypeMismatchEmail) return item.dataset.opMessageTypeMismatchEmail;
			if (item.type === 'email') return this.messages.messageTypeMismatchEmail.text;
			// URL
			if (item.dataset.opMessageTypeMismatchUrl) return item.dataset.opMessageTypeMismatchUrl;
			if (item.type === 'url') return this.messages.messageTypeMismatchUrl.text;
		}

		// If too short
		if (validity.tooShort) {
			if (item.dataset.opMessageTooShort) {
				if (item.dataset.opMessageTooShort.indexOf('{minLength}') > 0 && item.hasAttribute('minlength')) {
					return item.dataset.opMessageTooShort.replace('{minLength}', item.getAttribute('minlength')).replace('{length}', item.value.length);
				}
			}
			return this.messages.messageTooShort.text.replace('{minLength}', item.getAttribute('minlength')).replace('{length}', item.value.length);
		}

		// If too long
		if (validity.tooLong) {
			if (item.dataset.opMessageTooLong) {
				if (item.dataset.opMessageTooLong.indexOf('{maxLength}') > 0 && item.hasAttribute('maxlength')) {
					return item.dataset.opMessageTooLong.replace('{maxLength}', item.getAttribute('maxlength')).replace('{length}', item.value.length);
				}
			}
			return this.messages.messageTooLong.text.replace('{maxLength}', item.getAttribute('maxlength')).replace('{length}', item.value.length);
		}

		// If number input isn't a number
		if (validity.badInput) {
			if (item.dataset.opMessageBadInput) return item.dataset.opMessageBadInput;
			return this.messages.messageBadInput.text;
		}

		// If a number value doesn't match the step interval
		if (validity.stepMismatch) {
			if (item.dataset.opMessageStepMismatch) {
				if (item.dataset.opMessageStepMismatch.indexOf('{step}') > 0 && item.hasAttribute('step')) {
					return item.dataset.opMessageStepMismatch.replace('{step}', item.getAttribute('step'));
				}
			}
			return this.messages.messageStepMismatch.text.replace('{step}', item.getAttribute('step') || 1);
		}

		// If a number field is over the max
		if (validity.rangeOverflow) {
			if (item.dataset.opMessageRangeOverflow) {
				if (item.dataset.opMessageRangeOverflow.indexOf('{max}') > 0 && item.hasAttribute('max')) {
					return item.dataset.opMessageRangeOverflow.replace('{max}', item.getAttribute('max'));
				}
			}
			return this.messages.messageRangeOverflow.text.replace('{max}', item.getAttribute('max'));
		}

		// If a number field is below the min
		if (validity.rangeUnderflow) {
			if (item.dataset.opMessageRangeUnderflow) {
				if (item.dataset.opMessageRangeUnderflow.indexOf('{min}') > 0 && item.hasAttribute('min')) {
					return item.dataset.opMessageRangeUnderflow.replace('{min}', item.getAttribute('min'));
				}
			}
			return this.messages.messageRangeUnderflow.text.replace('{min}', item.getAttribute('min'));
		}

		// If pattern doesn't match
		if (validity.patternMismatch) {
			// If pattern does not match
			if (item.dataset.opMessagePatternMismatch) return item.dataset.opMessagePatternMismatch;
			return this.messages.messagePatternMismatch.text;
		}

		// Custom Error is Set?
		if (validity.customError) {
			if (item.dataset.opMessageValueMissing) return item.dataset.opMessageValueMissing;
			return this.messages.messageValueMissingSelect.text;
		}
	}

	showError(errObj) {
		errObj.forEach(formItem => {
			this.removeError(formItem.item);
			let message = document.createElement('div');
			message.className = 'form-field-error';
			message.htmlFor = formItem.item.id;
			message.innerHTML = formItem.validation;
			this.decorateFormElement(this.getFormElementWrapper(formItem.item), 'add', message);
		});
	}

	removeError(item) {
		this.decorateFormElement(this.getFormElementWrapper(item), 'remove');
	}

	resetForm(items) {
		Array.from(items).forEach(item => {
			this.removeError(item);
		})
		this.form.reset();
	}

	getFormElementWrapper(item) {
		let formElementWrapper = undefined;
		switch(item.type) {
		case 'radio':
		case 'checkbox':
			formElementWrapper = item.closest('.opc-form-field');
			break;
		default:
			formElementWrapper = item.closest('.opc-form-field');
		}
		return formElementWrapper;
	}

	decorateFormElement(item, decorationType = 'add', msg = null) {
		// do not validate recaptcha elements
		if (item === null || item.classList.contains('g-recaptcha-response')) {
			return ;
		}

		if (decorationType === 'add') {
			item.classList.add('is-invalid');

			// check for form field element
			let formFieldElement = item.querySelector('.form-field-element');
			if(formFieldElement) {
				formFieldElement.insertAdjacentElement('afterend', msg);
			} else {
				item.appendChild(msg);
			}
		} else {
			if (item.classList.contains('is-invalid')) {
				item.classList.remove('is-invalid');
				let errorElement = item.querySelector('.form-field-error');
				errorElement.remove();
			}
		}
	}

	/**
	 * Validierung der Formularfelder auf gültigen Input
	 *
	 * @param {HTMLElement} item Formularfeld
	 * @returns Array mit Fehlermeldungen
	 */
	validateInput(item) {
		let validationSet = [];

		if (!item.checkValidity()) {
			validationSet.push({ item: item, validation: this.getValidityDetails(item) });
		} else if (item.type === 'hidden' && item.hasAttribute('required')) {
			if (!item.dataset.opValue) {
				validationSet.push({ item: item, validation: this.messages.messageValueMissing.text });
			}
		} else if (item.dataset.opZipcityPair) {

			let zipCityFields = [];

			Array.from(item.form.querySelectorAll(`[data-op-zipcity-pair="${item.dataset.opZipcityPair}"]`)).forEach(i => {
				this.removeError(i);
				zipCityFields.push(i);
			});

			if(!this.checkZipCityCombination(zipCityFields)) {
				zipCityFields.forEach(field => {
					validationSet.push({ item: field, validation: field.dataset.opMessageBadCombination });
				});

			}
		} else if(item.dataset.opZipcitySearch){
			let value = item.value.trim();

			let zipCityField = this.zipSearch.inputFields.filter(obj => {
				return obj.inputField == item;
			});

            let zipCity = zipCityField[0].data.find(i => value === `${i.zip} ${i.city}`);

			if (zipCityField[0].isDirty === true && zipCity) {
				zipCityField[0].isDirty = false;
			}

			if(!zipCity || zipCityField[0].isDirty === true){
				let validate = true;
				if(zipCityField[0].isDirty === false && value !== null && value !== ''){
					validate = false;
				}

				if(validate){
					let msg = item.dataset.opMessageBadCombination;
					if(!msg){
						msg = this.messages.messageGeneric.text;
					}
					validationSet.push({ item: item, validation: msg });
				}
			}else{
				item.nextElementSibling.classList.remove('is-visible');
			}

			if(zipCityField[0].data.length == 0){
				item.nextElementSibling.classList.remove('is-visible');
			}
		} else if (item.dataset.opType == 'autocomplete') {
			let autocompleteWrapper = item.closest('.opc-autocomplete');
			if (autocompleteWrapper.classList.contains('is-loading')) {
				item.setCustomValidity('loading');
				validationSet.push({ item: item, validation: this.getValidityDetails(item) });
				item.setCustomValidity('');
			}
		} else if (item.dataset.opMatchInput) {
			let isMatchTrue = this.isMatchingInput(item);
			if (!isMatchTrue && isMatchTrue !== undefined) {
				validationSet.push({ item: item, validation: this.messages.messageUnequal.text });
				validationSet.push({ item: this.form.querySelector(item.dataset.opMatchInput), validation: this.messages.messageUnequal.text });
			}
		}

		return new Promise((resolve) => {
			resolve(validationSet);
		});
	}

	checkZipCityCombination(allZipCityFields) {
		const zipCityValidation = {
			currentZip: '',
			currentCity: ''
		};

		allZipCityFields.forEach(field => {
			let pairingType = field.dataset.opPairingType;
			if (pairingType) {
				switch (pairingType.toLowerCase()) {
				case 'zip':
					zipCityValidation.currentZip = field;
					break;
				case 'city':
					zipCityValidation.currentCity = field;
					break;
				case 'country':
					zipCityValidation.currentCountry = field;
					break;
				default:
					break;
				}
			} else if (field.id === `${field.form.id}-zip`) {
				zipCityValidation.currentZip = field;
			} else if (field.id === `${field.form.id}-city`){
				zipCityValidation.currentCity = field;
			}
		});

		let hasNoInputAmout = 0;
		for (const [, element] of Object.entries(zipCityValidation)) {
			if (element.value === '') {
				hasNoInputAmout++;
			}
		}

		if (hasNoInputAmout === Object.keys(zipCityValidation).length) {
			return true;
		}

		let isValid = false;
		let fieldValidationObj = {
			zipIsValid: false,
			cityIsValid: false
		};

		let city = this.getObjects(window.zipCityCombination, '', zipCityValidation.currentCity.value.toLowerCase());
		if (city.length > 0){
			city.some(item => {
				if (item.Zip === zipCityValidation.currentZip.value.toString()) {
					fieldValidationObj.zipIsValid = true;
				}
				if (zipCityValidation.currentCountry) {
					if (item.Country.toLowerCase() === zipCityValidation.currentCountry.value.toLowerCase()) {
						fieldValidationObj.countryIsValid = true;
					}
				}
				if (item.City.toLowerCase() === zipCityValidation.currentCity.value.toLowerCase()) {
					fieldValidationObj.cityIsValid = true;
				}
			});

			hasNoInputAmout = 0;
			for (const [, value] of Object.entries(fieldValidationObj)) {
				if (value) {
					hasNoInputAmout++;
				}
			}

			if (hasNoInputAmout === Object.keys(zipCityValidation).length) {
				isValid = true;
			}
		}

		return isValid;
	}

	getObjects(obj, key, val) {
		let objects = [];
		for (let i in obj) {
			// eslint-disable-next-line no-prototype-builtins
			if (!obj.hasOwnProperty(i)) continue;
			if (typeof obj[i] == 'object') {
				objects = objects.concat(this.getObjects(obj[i], key, val));
			} else
			if (i == key && obj[i] == val || i == key && val == '') {
				objects.push(obj);
			} else if (obj[i] == val && key == ''){
				if (objects.lastIndexOf(obj) == -1){
					objects.push(obj);
				}
			}
		}
		return objects;
	}

	checkGRecaptcha(form) {
		let returnObj = {};
		let gRecaptchaElem = form.querySelector('.g-recaptcha') || undefined;
		let isInvisibleCaptcha =  undefined;
		let gRecaptchaApi = undefined;
		let hasDedicatedCheck = undefined;
		let reCaptchaVersion = 'v2';
        let reCaptchaResponseElem = form.querySelector('#g-recaptcha-response') || undefined;

		if (gRecaptchaElem) {
			isInvisibleCaptcha = gRecaptchaElem.dataset.size == 'invisible' ? true : false;
			gRecaptchaApi = window.grecaptcha ? true : false;
			hasDedicatedCheck = false;
		} else if (form.querySelector('input.g-recaptcha-response[type="hidden"]')) {
			hasDedicatedCheck = true;
            reCaptchaVersion = form.querySelector('input.g-recaptcha-response[type="hidden"]').getAttribute('data-op-recaptcha-version');
		}

		returnObj.gRecaptchaElem = gRecaptchaElem;
		returnObj.gRecaptchaApi = gRecaptchaApi;
		returnObj.isInvisibleCaptcha = isInvisibleCaptcha;
		returnObj.hasDedicatedCheck = hasDedicatedCheck;
		returnObj.reCaptchaVersion = reCaptchaVersion;
		returnObj.reCaptchaResponse = reCaptchaResponseElem ? reCaptchaResponseElem.value : '';

		return returnObj;
	}

	static isFormValid(form, showErrors = false) {
		let retVal = false;
		const formValidator = new OpaccFormValidator(form);
        return new Promise((resolve) => {
        formValidator.validateForm(form.elements).then(result => {
            if (result.length === 0) {
                retVal = true;
				}

				if (showErrors && result.length > 0) {
                    formValidator.showError(result);
					// set focus on first formElement with Error
					result[0].item.focus();
				}

				resolve(retVal);
			});
		});
	}
}